import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogTrigger,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { RefreshCw, Loader2, Check, AlertTriangle, Package, Sparkles, Briefcase } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SquareSyncButton() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busyType, setBusyType] = useState(null); // 'full' | 'quick' | 'services'
  const [lastResult, setLastResult] = useState(null);

  const runSync = async (type) => {
    if (loading) return;
    setLoading(true);
    setBusyType(type);
    setLastResult(null);

    const endpoints = {
      full: { url: '/square/sync', label: 'Synchronisation complète' },
      quick: { url: '/square/sync-quick', label: 'Synchronisation rapide' },
      services: { url: '/square/sync-services', label: 'Synchronisation des services' },
    };
    const ep = endpoints[type];
    const t0 = Date.now();

    // Helper qui suit un job_id jusqu'à done/failed et affiche le résultat
    const handleJob = async (jobId) => {
      const result = await pollSyncStatus(jobId);
      const duration = Math.round((Date.now() - t0) / 1000);
      if (result.status === 'done') {
        setLastResult({
          type, success: true,
          imported: result.imported || 0,
          updated: result.updated || 0,
          errors: result.errors || 0,
          deactivated: result.deactivated || 0,
          duration,
        });
        toast.success(`${ep.label} terminée : ${result.imported || 0} ajoutés, ${result.updated || 0} mis à jour`);
      } else {
        setLastResult({ type, success: false, error: result.error || "Échec inconnu" });
        toast.error("Synchronisation échouée");
      }
    };

    try {
      // Timeout court — le backend doit répondre en <5s avec un job_id.
      // Si Cloudflare 524 ou network glitch, on fallback sur /square/sync/latest/{type}.
      const { data } = await axios.post(`${API}${ep.url}`, {}, {
        withCredentials: true,
        timeout: 15000,
      });

      if (data.job_id) {
        toast.info(`${ep.label} lancée en arrière-plan…`);
        await handleJob(data.job_id);
      } else {
        // Fallback ancien format synchrone (ne devrait plus arriver)
        const duration = Math.round((Date.now() - t0) / 1000);
        setLastResult({
          type, success: true,
          imported: data.imported || 0,
          updated: data.updated || 0,
          errors: data.errors || 0,
          duration,
        });
        toast.success(`${ep.label} terminée : ${data.imported || 0} ajoutés, ${data.updated || 0} mis à jour`);
      }
    } catch (err) {
      // Cas Cloudflare 524 / timeout / network : on cherche si un job vient d'être créé
      // dans les 30 dernières secondes pour ce type et on bascule sur le polling.
      try {
        const { data: latest } = await axios.get(`${API}/square/sync/latest/${type}`, {
          withCredentials: true,
          timeout: 8000,
        });
        const startedRecently = latest?.started_at
          && (Date.now() - new Date(latest.started_at).getTime()) < 60 * 1000;
        if (latest?.job_id && (latest.status === 'running' || startedRecently)) {
          toast.info(`${ep.label} en cours côté serveur — on suit…`);
          await handleJob(latest.job_id);
          return;
        }
      } catch (_e) { /* pas de job trouvé → on affiche l'erreur d'origine */ }

      const msg = err?.response?.data?.detail || err?.message || "Erreur de synchronisation";
      setLastResult({ type, success: false, error: typeof msg === 'string' ? msg : 'Erreur' });
      toast.error("Échec : " + (typeof msg === 'string' ? msg : 'voir détails'));
    } finally {
      setLoading(false);
      setBusyType(null);
    }
  };

  // Poll le statut du job. Démarrage rapide (2s) pour les syncs courtes,
  // puis backoff progressif jusqu'à 5s pour la sync complète IA (5+ min).
  const pollSyncStatus = async (jobId) => {
    const start = Date.now();
    const maxDurationMs = 10 * 60 * 1000; // 10 min max
    let interval = 2000;
    while (Date.now() - start < maxDurationMs) {
      await new Promise(r => setTimeout(r, interval));
      try {
        const { data } = await axios.get(`${API}/square/sync/status/${jobId}`, { withCredentials: true });
        if (data.status === 'done' || data.status === 'failed') return data;
      } catch (_e) { /* on continue */ }
      // Backoff doux : 2s → 3s → 5s
      if (interval < 5000) interval = Math.min(5000, interval + 1000);
    }
    return { status: 'failed', error: "Timeout : la sync dépasse 10 min. Vérifie les logs serveur." };
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !loading && setOpen(v)}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 gap-2"
          data-testid="square-sync-button"
        >
          <RefreshCw className="w-4 h-4" />
          Sync Square
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#261B3B] border-white/10 max-w-lg" data-testid="square-sync-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-white flex items-center gap-2">
            <RefreshCw className={`w-5 h-5 text-[#00E5FF] ${loading ? 'animate-spin' : ''}`} />
            Synchronisation Square
          </DialogTitle>
          <DialogDescription className="text-sm text-[#B59ED1]">
            Force la mise à jour du catalogue et des stocks depuis Square.
            La synchronisation automatique tourne toutes les 30 minutes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Sync rapide */}
          <button
            type="button"
            disabled={loading}
            onClick={() => runSync('quick')}
            className="w-full text-left p-4 bg-[#1B132B] rounded-lg border border-white/5 hover:border-[#00E5FF]/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors group"
            data-testid="square-sync-quick"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#00E5FF]/10 flex items-center justify-center shrink-0">
                {busyType === 'quick' ? <Loader2 className="w-5 h-5 animate-spin text-[#00E5FF]" /> : <Package className="w-5 h-5 text-[#00E5FF]" />}
              </div>
              <div className="flex-1">
                <p className="font-medium text-white">Synchronisation rapide</p>
                <p className="text-xs text-[#B59ED1] mt-0.5">Prix &amp; stocks uniquement. ~10-30 sec. Recommandé pour un usage quotidien.</p>
              </div>
            </div>
          </button>

          {/* Sync complète */}
          <button
            type="button"
            disabled={loading}
            onClick={() => runSync('full')}
            className="w-full text-left p-4 bg-[#1B132B] rounded-lg border border-white/5 hover:border-[#D926A9]/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors group"
            data-testid="square-sync-full"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#D926A9]/10 flex items-center justify-center shrink-0">
                {busyType === 'full' ? <Loader2 className="w-5 h-5 animate-spin text-[#D926A9]" /> : <Sparkles className="w-5 h-5 text-[#D926A9]" />}
              </div>
              <div className="flex-1">
                <p className="font-medium text-white">Synchronisation complète (avec IA)</p>
                <p className="text-xs text-[#B59ED1] mt-0.5">Réécrit les descriptions et le SEO via IA. Plus long (2-5 min). À utiliser après import massif.</p>
              </div>
            </div>
          </button>

          {/* Sync services */}
          <button
            type="button"
            disabled={loading}
            onClick={() => runSync('services')}
            className="w-full text-left p-4 bg-[#1B132B] rounded-lg border border-white/5 hover:border-[#9D4CDD]/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors group"
            data-testid="square-sync-services"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#9D4CDD]/10 flex items-center justify-center shrink-0">
                {busyType === 'services' ? <Loader2 className="w-5 h-5 animate-spin text-[#9D4CDD]" /> : <Briefcase className="w-5 h-5 text-[#9D4CDD]" />}
              </div>
              <div className="flex-1">
                <p className="font-medium text-white">Synchroniser les services / prestations</p>
                <p className="text-xs text-[#B59ED1] mt-0.5">Met à jour les services (voyance, soins…) depuis Square Appointments.</p>
              </div>
            </div>
          </button>
        </div>

        {/* Résultat */}
        {lastResult && (
          <div
            className={`mt-4 p-4 rounded-lg border ${lastResult.success ? 'bg-green-500/5 border-green-500/30' : 'bg-red-500/5 border-red-500/30'}`}
            data-testid="square-sync-result"
          >
            <div className="flex items-start gap-3">
              {lastResult.success ? (
                <Check className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              )}
              <div className="flex-1 text-sm">
                {lastResult.success ? (
                  <>
                    <p className="text-green-400 font-medium mb-1">Synchronisation terminée</p>
                    <p className="text-[#E2D5F8]">
                      <span className="text-white font-semibold">{lastResult.imported}</span> ajoutés &middot;{' '}
                      <span className="text-white font-semibold">{lastResult.updated}</span> mis à jour
                      {lastResult.errors > 0 && (
                        <> &middot; <span className="text-orange-400 font-semibold">{lastResult.errors}</span> erreurs</>
                      )}
                    </p>
                    <p className="text-xs text-[#B59ED1] mt-1">Durée : {lastResult.duration}s</p>
                  </>
                ) : (
                  <>
                    <p className="text-red-400 font-medium mb-1">Échec</p>
                    <p className="text-[#E2D5F8]">{lastResult.error}</p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {loading && (
          <p className="text-xs text-center text-[#B59ED1] mt-2">
            Ne ferme pas cette fenêtre pendant la synchronisation…
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
